/* === UPDATED FILE: src/pages/courses/TestPlayer.js === */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const TestPlayer = ({ lectureId, onTestComplete }) => {
    const [testData, setTestData] = useState(null);
    const [userAnswers, setUserAnswers] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submissionResult, setSubmissionResult] = useState(null);

    const fetchTest = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        setSubmissionResult(null);
        setUserAnswers({});
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.get(`/api/courses/lectures/${lectureId}/test`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTestData(response.data);
        } catch (err) {
            setError("Failed to load the test. Please try again.");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [lectureId]);
    
    useEffect(() => {
        fetchTest();
    }, [fetchTest]);

    const totalPossibleScore = useMemo(() => {
        if (!testData || !testData.questions) return 0;
        return testData.questions.reduce((total, q) => total + (q.points || 0), 0);
    }, [testData]);

    const handleAnswerChange = (questionId, optionId, questionType) => {
        const newAnswers = { ...userAnswers };
        const currentAnswers = newAnswers[questionId] || [];

        if (questionType === 'mcq-single' || questionType === 'true-false') {
            newAnswers[questionId] = [optionId];
        } else if (questionType === 'mcq-multiple') {
            if (currentAnswers.includes(optionId)) {
                newAnswers[questionId] = currentAnswers.filter(id => id !== optionId);
            } else {
                newAnswers[questionId] = [...currentAnswers, optionId];
            }
        } else { // fill-in-blank
            newAnswers[questionId] = [optionId]; 
        }
        setUserAnswers(newAnswers);
    };

    const handleSubmit = async () => {
        if (!window.confirm("Are you sure you want to submit your answers?")) return;
        
        setIsLoading(true);
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.post(`/api/courses/tests/${testData.id}/submit`, 
                { answers: userAnswers },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSubmissionResult(response.data);
            onTestComplete(response.data.passed);
        } catch (err) {
            setError("Failed to submit the test.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading && !submissionResult) return <p>Loading test...</p>;
    if (error) return <p className="error-message">{error}</p>;
    
    // If the user has already passed, show a special results card.
    if (testData?.alreadyPassed) {
        return (
            <div className="test-results-card pass">
                <span className="material-icons results-icon pass">task_alt</span>
                <h2 className="results-title">Test Already Passed</h2>
                <p className="results-score">
                    {/* --- FIX: Convert score to a number before calling .toFixed() --- */}
                    Your Score: <strong>{parseFloat(testData.score).toFixed(0)}%</strong>
                </p>
                <p className="results-message">You have successfully completed this test. The next lecture is unlocked.</p>
            </div>
        );
    }
    
    // If the user just submitted their answers, show the results.
    if (submissionResult) {
        return (
            <div className={`test-results-card ${submissionResult.passed ? 'pass' : 'fail'}`}>
                <span className={`material-icons results-icon ${submissionResult.passed ? 'pass' : 'fail'}`}>
                    {submissionResult.passed ? 'check_circle' : 'cancel'}
                </span>
                <h2 className="results-title">Test Complete</h2>
                <p className="results-score">
                    Your Score: <strong>{submissionResult.userScore.toFixed(1)} / {submissionResult.totalPossibleScore} ({submissionResult.score.toFixed(0)}%)</strong>
                </p>
                {submissionResult.passed ? (
                    <p className="results-message">Congratulations, you passed! The next lecture is now unlocked.</p>
                ) : (
                    <p className="results-message">You did not meet the passing score. Please review the material and try again.</p>
                )}
                <div className="results-actions">
                    {!submissionResult.passed && (
                        <button onClick={fetchTest} className="btn-primary">Try Again</button>
                    )}
                </div>
            </div>
        );
    }
    
    if (!testData) return <p>No test available for this lecture.</p>;

    // Default view: Show the test questions for the user to take.
    return (
        <div className="test-player-container" style={{padding: '1.5rem', background: 'var(--admin-sidebar-bg)', borderRadius: '12px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                <h3>{testData.title}</h3>
                <p><strong>Total Points: {totalPossibleScore}</strong></p>
            </div>
            {testData.questions.map((q, index) => (
                <div key={q.id} className="question-card">
                    <div style={{display: 'flex', justifyContent: 'space-between'}}>
                        <p><strong>Question {index + 1}</strong></p>
                        <p><strong>({q.points || 0} points)</strong></p>
                    </div>
                    <p>{q.question_text}</p>
                    <div className="options-container">
                        {q.question_type === 'fill-in-blank' ? (
                            <input 
                                type="text"
                                className="styled-input"
                                placeholder="Type your answer here..."
                                onChange={(e) => handleAnswerChange(q.id, e.target.value, q.question_type)}
                            />
                        ) : q.options.map(opt => (
                            <div key={opt.id}>
                                <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                    <input 
                                        type={q.question_type === 'mcq-multiple' ? 'checkbox' : 'radio'}
                                        name={`question-${q.id}`}
                                        checked={(userAnswers[q.id] || []).includes(opt.id)}
                                        onChange={() => handleAnswerChange(q.id, opt.id, q.question_type)}
                                    />
                                    {opt.option_text}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            <button onClick={handleSubmit} className="btn-primary" style={{marginTop: '1rem'}}>Submit Test</button>
        </div>
    );
};

export default TestPlayer;
